import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  methodologyService,
  type MethodologyNode,
  type MethodologyPage,
} from '../services/methodology/methodologyService';
import { queryKeyFactory } from '../services/core/queryKeys';

export function useMethodologyTree({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<MethodologyNode[]>({
    queryKey: queryKeyFactory.methodology.tree(),
    queryFn: methodologyService.getTree,
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useMethodologyPage(id: string | undefined) {
  return useQuery<MethodologyPage>({
    queryKey: queryKeyFactory.methodology.page(id ?? ''),
    queryFn: () => methodologyService.getPage(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

function useInvalidateMethodology() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeyFactory.methodology.all });
}

export function useCreateMethodologyPage() {
  const invalidate = useInvalidateMethodology();
  return useMutation({
    mutationFn: methodologyService.createPage,
    onSuccess: invalidate,
  });
}

export function useUpdateMethodologyPage() {
  const invalidate = useInvalidateMethodology();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; title?: string; content?: string }) =>
      methodologyService.updatePage(id, data),
    onSuccess: invalidate,
  });
}

export function useMoveMethodologyPage() {
  const invalidate = useInvalidateMethodology();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; parent_id: string | null; index: number }) =>
      methodologyService.movePage(id, data),
    // Дерево перерисовываем и при ошибке — чтобы откатить оптимистичный drag-n-drop в Tree
    onSettled: invalidate,
  });
}

export function useDeleteMethodologyPage() {
  const invalidate = useInvalidateMethodology();
  return useMutation({
    mutationFn: (id: string) => methodologyService.deletePage(id),
    onSuccess: invalidate,
  });
}
